import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { ApprovalFlow, ApprovalRequest, ApprovalRuleProjectAmount, ApprovalStep, PendingApprovalStep, ResolvedApprovalStep } from '../models/approval-engine.models';

@Injectable({
  providedIn: 'root'
})
export class ApprovalEngineService {
  private apiUrl = `${environment.apiEndPoint}/api/ApprovalFlows`;
  private stepsUrl = `${environment.apiEndPoint}/api/approval-steps`;
  private requestsUrl = `${environment.apiEndPoint}/api/approval-requests`;

  constructor(private http: HttpClient) { }

  getFlows(): Observable<ApprovalFlow[]> {
    return this.http.get<ApprovalFlow[]>(this.apiUrl);
  }

  getFlowById(id: number): Observable<ApprovalFlow> {
    return this.http.get<ApprovalFlow>(`${this.apiUrl}/${id}`);
  }

  createFlow(flow: ApprovalFlow): Observable<ApprovalFlow> {
    return this.http.post<ApprovalFlow>(this.apiUrl, flow);
  }

  updateFlow(id: number, flow: ApprovalFlow): Observable<ApprovalFlow> {
    return this.http.put<ApprovalFlow>(`${this.apiUrl}/${id}`, flow);
  }

  deleteFlow(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // Bandeja del usuario autenticado: pasos pendientes cuyo cargo ocupa.
  getMyPendingSteps(): Observable<PendingApprovalStep[]> {
    return this.http.get<PendingApprovalStep[]>(`${this.stepsUrl}/mine/pending`);
  }

  // Historial del usuario autenticado: lo que ya aprobó o rechazó.
  getMyResolvedSteps(take = 50): Observable<ResolvedApprovalStep[]> {
    return this.http.get<ResolvedApprovalStep[]>(`${this.stepsUrl}/mine/resolved?take=${take}`);
  }

  // Aprueba o rechaza un paso. El aprobador se resuelve del usuario autenticado.
  processStep(stepId: number, isApproved: boolean, comments?: string): Observable<ApprovalStep> {
    return this.http.post<ApprovalStep>(`${this.stepsUrl}/${stepId}/process`, { isApproved, comments });
  }

  // Tramos del flujo para un proyecto. Sin override devuelve los montos por defecto del flujo.
  getProjectAmounts(flowId: number, projectId: number): Observable<ApprovalRuleProjectAmount[]> {
    return this.http.get<ApprovalRuleProjectAmount[]>(
      `${this.apiUrl}/${flowId}/project-amounts`, { params: { projectId } });
  }

  // Reemplaza los tramos de un proyecto. El backend valida solapes y huecos: un hueco haría que
  // el motor no encuentre regla y apruebe la orden sola.
  saveProjectAmounts(flowId: number, projectId: number, amounts: ApprovalRuleProjectAmount[]): Observable<ApprovalRuleProjectAmount[]> {
    return this.http.put<ApprovalRuleProjectAmount[]>(
      `${this.apiUrl}/${flowId}/project-amounts`, { projectId, amounts });
  }

  // Solicitud de aprobación asociada a una entidad concreta (p. ej. entityType=PayrollPeriod, targetId=42).
  getRequestByTarget(entityType: string, targetId: number): Observable<ApprovalRequest> {
    return this.http.get<ApprovalRequest>(`${this.requestsUrl}/by-target`, { params: { entityType, targetId } });
  }
}
